import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { BadgeCheck, ChevronLeft, ChevronRight, Search, ShieldAlert, XCircle } from "lucide-react";
import { apiGet, apiPatch } from "./api";
import { queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Textarea } from "@/components/ui/textarea";

type ClaimDecision = "approved" | "rejected" | "disputed" | "revoked";

type Claim = {
  id: string;
  business_id: string;
  business_name: string;
  business_name_ko: string | null;
  phone: string | null;
  address: string | null;
  suburb: string | null;
  state: string | null;
  postcode: string | null;
  applicant_name: string | null;
  applicant_email: string;
  status: string;
  verification_method: string;
  verification_target_masked: string | null;
  applicant_note: string | null;
  decision_reason: string | null;
  publication_status: string;
  claim_status: string;
  verification_status: string;
  owner_user_id: string | null;
  created_at: string;
};

type ClaimsResponse = {
  items: Claim[];
  total: number;
  summary: Record<string, number | null>;
};

const statuses = ["claim_pending", "disputed", "approved", "rejected", "revoked", "all"];
const statusLabel: Record<string, string> = {
  all: "전체",
  claim_pending: "승인 대기",
  disputed: "소유권 분쟁",
  approved: "승인",
  rejected: "거절",
  revoked: "회수",
};
const methodLabel: Record<string, string> = {
  domain_email: "업체 도메인 이메일",
  phone_otp: "전화 OTP",
  phone_callback: "전화 콜백",
  abn_document: "ABN·사업자 서류",
  manual_review: "관리자 수동 검토",
};

export default function BusinessClaimsManagement() {
  const { toast } = useToast();
  const [status, setStatus] = useState("claim_pending");
  const [search, setSearch] = useState("");
  const [query, setQuery] = useState("");
  const [offset, setOffset] = useState(0);
  const [decisionTarget, setDecisionTarget] = useState<{ claim: Claim; decision: ClaimDecision } | null>(null);
  const [reason, setReason] = useState("");
  const limit = 50;
  const params = new URLSearchParams({ status, q: query, limit: String(limit), offset: String(offset) });

  const claims = useQuery({
    queryKey: ["admin-business-claims", status, query, offset],
    queryFn: () => apiGet<ClaimsResponse>(`/api/admin/business-claims?${params}`),
  });
  const decide = useMutation({
    mutationFn: ({ id, decision, reason }: { id: string; decision: ClaimDecision; reason: string }) =>
      apiPatch(`/api/admin/business-claims/${encodeURIComponent(id)}`, { decision, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-business-claims"] });
      toast({ title: "Claim 처리 완료", description: "업체 소유권과 검증 감사 기록을 함께 갱신했습니다." });
      setDecisionTarget(null);
      setReason("");
    },
    onError: (error: Error) => toast({ title: "Claim 처리 실패", description: error.message, variant: "destructive" }),
  });

  const openDecision = (claim: Claim, decision: ClaimDecision) => {
    setDecisionTarget({ claim, decision });
    setReason("");
  };
  const summary = claims.data?.summary || {};
  const items = claims.data?.items || [];

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">업체 소유권 요청</h2>
        <p className="text-sm text-muted-foreground">승인 전에는 업체 정보와 소유자가 변경되지 않습니다. 분쟁 요청을 먼저 검토하세요.</p>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">승인 대기</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{Number(summary.claim_pending || 0)}</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">분쟁</CardTitle></CardHeader><CardContent className="text-2xl font-bold text-amber-600">{Number(summary.disputed || 0)}</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">승인</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{Number(summary.approved || 0)}</CardContent></Card>
        <Card><CardHeader className="pb-2"><CardTitle className="text-sm">거절</CardTitle></CardHeader><CardContent className="text-2xl font-bold">{Number(summary.rejected || 0)}</CardContent></Card>
      </div>

      <Card>
        <CardHeader><CardTitle>검토 큐</CardTitle></CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-3">
            <Select value={status} onValueChange={(value) => { setStatus(value); setOffset(0); }}>
              <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
              <SelectContent>{statuses.map((value) => <SelectItem key={value} value={value}>{statusLabel[value]}</SelectItem>)}</SelectContent>
            </Select>
            <div className="flex min-w-64 flex-1 gap-2">
              <Input value={search} onChange={(event) => setSearch(event.target.value)} onKeyDown={(event) => { if (event.key === "Enter") { setQuery(search.trim()); setOffset(0); } }} placeholder="업체명, 전화, 주소, 신청자 이메일" />
              <Button variant="outline" onClick={() => { setQuery(search.trim()); setOffset(0); }}><Search className="mr-1 h-4 w-4" />검색</Button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow><TableHead>업체</TableHead><TableHead>신청자</TableHead><TableHead>확인 방법</TableHead><TableHead>상태</TableHead><TableHead className="text-right">작업</TableHead></TableRow></TableHeader>
              <TableBody>
                {items.map((claim) => (
                  <TableRow key={claim.id}>
                    <TableCell className="max-w-sm">
                      <div className="font-medium">{claim.business_name_ko || claim.business_name}</div>
                      <div className="text-xs text-muted-foreground">{claim.phone || "전화 없음"} · {[claim.suburb, claim.state, claim.postcode].filter(Boolean).join(" ")}</div>
                      <div className="truncate text-xs text-muted-foreground">{claim.address}</div>
                      <div className="mt-1 flex gap-1"><Badge variant="outline">{claim.publication_status}</Badge><Badge variant="outline">{claim.verification_status}</Badge></div>
                    </TableCell>
                    <TableCell><div>{claim.applicant_name || "이름 없음"}</div><div className="text-xs text-muted-foreground">{claim.applicant_email}</div><div className="mt-1 text-xs">{claim.applicant_note}</div></TableCell>
                    <TableCell><div>{methodLabel[claim.verification_method] || claim.verification_method}</div><div className="text-xs text-muted-foreground">{claim.verification_target_masked || "증빙 확인 필요"}</div></TableCell>
                    <TableCell><Badge variant={claim.status === "disputed" ? "destructive" : "secondary"}>{statusLabel[claim.status] || claim.status}</Badge><div className="mt-1 text-xs text-muted-foreground">{new Date(claim.created_at).toLocaleDateString("ko-KR")}</div>{claim.decision_reason && <div className="mt-1 max-w-xs text-xs">{claim.decision_reason}</div>}</TableCell>
                    <TableCell><div className="flex justify-end gap-1">
                      {(claim.status === "claim_pending" || claim.status === "disputed") && <>
                        <Button size="sm" onClick={() => openDecision(claim, "approved")}><BadgeCheck className="mr-1 h-4 w-4" />승인</Button>
                        <Button size="sm" variant="outline" onClick={() => openDecision(claim, "disputed")}><ShieldAlert className="h-4 w-4" /></Button>
                        <Button size="sm" variant="outline" onClick={() => openDecision(claim, "rejected")}><XCircle className="h-4 w-4" /></Button>
                      </>}
                      {claim.status === "approved" && <Button size="sm" variant="destructive" onClick={() => openDecision(claim, "revoked")}>소유권 회수</Button>}
                    </div></TableCell>
                  </TableRow>
                ))}
                {!claims.isLoading && items.length === 0 && <TableRow><TableCell colSpan={5} className="py-10 text-center text-muted-foreground">해당 Claim이 없습니다.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </div>

          <div className="flex items-center justify-between">
            <Button variant="outline" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - limit))}><ChevronLeft className="mr-1 h-4 w-4" />이전</Button>
            <span className="text-sm text-muted-foreground">총 {claims.data?.total || 0}건</span>
            <Button variant="outline" disabled={items.length < limit} onClick={() => setOffset(offset + limit)}>다음<ChevronRight className="ml-1 h-4 w-4" /></Button>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!decisionTarget} onOpenChange={(open) => { if (!open) setDecisionTarget(null); }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{decisionTarget ? statusLabel[decisionTarget.decision] : "Claim 처리"}</DialogTitle>
            <DialogDescription>{decisionTarget?.claim.business_name}의 소유권 요청을 처리합니다. 사유는 신청자 상태 화면과 감사 기록에 남습니다.</DialogDescription>
          </DialogHeader>
          <div className="space-y-2"><Label htmlFor="claim-reason">처리 사유 *</Label><Textarea id="claim-reason" value={reason} onChange={(event) => setReason(event.target.value)} placeholder="확인한 증빙과 판단 근거를 기록하세요." /></div>
          <DialogFooter><Button variant="outline" onClick={() => setDecisionTarget(null)}>취소</Button><Button disabled={!reason.trim() || decide.isPending} onClick={() => decisionTarget && decide.mutate({ id: decisionTarget.claim.id, decision: decisionTarget.decision, reason: reason.trim() })}>확정</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
